/** Single-select tag-category picker used across the integration config widgets.
 *
 * A `freeSolo` Autocomplete over the TO's full tag-category list: a code may
 * not exist yet, and a deliberately non-existent code is a *valid* entry, so we
 * must accept free text as well as list selections. The stored value is always
 * the category `code` (a string), never the numeric id.
 */
import { Autocomplete, Box, TextField, Typography } from '@mui/material';

import type { TagCategory } from '@/api/integrations';

import { useTagCategories } from './hooks';

interface Props {
  toId: number;
  value: string | null;
  onChange: (code: string | null) => void;
  readOnly?: boolean;
  label?: string;
  placeholder?: string;
  size?: 'small';
}

/** Coerce whatever the freeSolo Autocomplete hands back into a code string. */
function toCode(v: string | TagCategory | null): string | null {
  if (v == null) return null;
  const code = typeof v === 'string' ? v : v.code;
  const trimmed = code.trim();
  return trimmed || null;
}

export function TagCategoryAutocomplete({
  toId,
  value,
  onChange,
  readOnly = false,
  label,
  placeholder,
  size = 'small',
}: Props) {
  const { data: categories, isLoading } = useTagCategories(toId);
  const options = categories ?? [];

  // Resolve the current code to its option so the dropdown highlights it; if the
  // code isn't a known category we still show it (freeSolo keeps the raw string).
  const selected = value != null ? (options.find((c) => c.code === value) ?? value) : null;

  return (
    <Autocomplete<TagCategory, false, false, true>
      freeSolo
      size={size}
      disabled={readOnly}
      loading={isLoading}
      options={options}
      value={selected}
      onChange={(_, v) => onChange(toCode(v))}
      // Free text isn't captured by onChange until commit; mirror the typed
      // value on blur so a hand-entered code is persisted.
      onInputChange={(_, input, reason) => {
        if (reason === 'input' || reason === 'clear') onChange(toCode(input));
      }}
      getOptionLabel={(o) => (typeof o === 'string' ? o : o.code)}
      isOptionEqualToValue={(a, b) => {
        const ac = typeof a === 'string' ? a : a.code;
        const bc = typeof b === 'string' ? b : b.code;
        return ac === bc;
      }}
      // Let the popup size to its content (code + full label) instead of the
      // (often narrow) input width, so labels aren't truncated.
      slotProps={{
        popper: {
          style: { width: 'fit-content' },
          sx: { minWidth: 280, maxWidth: 520 },
        },
      }}
      renderOption={(props, o) => {
        const cat = o as TagCategory;
        return (
          <Box
            component="li"
            {...props}
            key={cat.id}
            sx={{ fontSize: 13, display: 'flex', gap: 1, whiteSpace: 'nowrap' }}
          >
            <Box component="span" sx={{ fontFamily: 'monospace' }}>
              {cat.code}
            </Box>
            <Typography variant="caption" color="text.secondary">
              {cat.name}
            </Typography>
            {cat.is_inherited && (
              <Typography variant="caption" color="text.disabled">
                (inherited)
              </Typography>
            )}
          </Box>
        );
      }}
      renderInput={(params) => <TextField {...params} label={label} placeholder={placeholder} />}
    />
  );
}
